// UserDetails.js - User Details Modal Component
import React, { useEffect, useState } from "react";
import {
  Modal,
  Card,
  Row,
  Col,
  Tag,
  Button,
  Descriptions,
  Divider,
  Spin,
  Empty,
  message,
} from "antd";
import {
  UserIcon,
  MailIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  ShoppingBagIcon,
  CheckCircleIcon,
  XCircleIcon,
  FileTextIcon,
} from "lucide-react";
import { API_METHODS } from "../../../constants";
import useApi from "../../../hooks/useApi";

function UserDetails({
  visible,
  userId,
  onClose,
  onUpdate,
  onViewOrderHistory,
}) {
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();

  // API hooks
  const [userResponse, getUserRequest] = useApi();
  const [toggleResponse, toggleUserRequest] = useApi();

  // Fetch user details when modal opens
  useEffect(() => {
    if (visible && userId) {
      getUserRequest(null, API_METHODS.GET, `/admin/users/details/${userId}`);
    }
  }, [visible, userId]);

  // Handle toggle status response
  useEffect(() => {
    if (toggleResponse.data && !toggleResponse.isLoading) {
      messageApi.success("User status updated successfully!");
      onUpdate(); // Refresh parent data
    } else if (toggleResponse.error) {
      messageApi.error(toggleResponse.error || "Failed to update user status");
    }
  }, [toggleResponse]);

  // Handle toggle user status
  const handleToggleStatus = (action) => {
    const user = userResponse.data?.user;
    if (!user) return;

    const userName = [user.first_name, user.last_name].filter(Boolean).join(" ");

    modal.confirm({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
      content: `Are you sure you want to ${action} user: ${userName}?`,
      okText: "Yes",
      cancelText: "Cancel",
      okButtonProps: {
        style: action === "disable"
          ? { backgroundColor: "#ff4d4f", borderColor: "#ff4d4f", color: "#fff" }
          : { backgroundColor: "#52c41a", borderColor: "#52c41a", color: "#fff" },
      },
      onOk: () => {
        toggleUserRequest(
          { action },
          API_METHODS.PUT,
          `/admin/users/status/${userId}`
        );
      },
    });
  };

  const user = userResponse.data?.user;
  const recentOrders = userResponse.data?.recent_orders || [];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
        return "green";
      case "completed":
        return "blue";
      case "pending":
        return "orange";
      case "cancelled":
        return "red";
      default:
        return "gray";
    }
  };

  return (
    <>
      {contextHolder}
      {modalContextHolder}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <UserIcon style={{ marginRight: "8px", color: "#f97316" }} />
            User Details
          </div>
        }
        open={visible}
        onCancel={onClose}
        width={800}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
          user && (
            <Button
              key="orders"
              icon={<FileTextIcon className="h-4 w-4" />}
              onClick={() => {
                onClose();
                onViewOrderHistory(userId);
              }}
              disabled={!user.total_orders || user.total_orders === 0}
            >
              View Order History ({user.total_orders || 0})
            </Button>
          ),
          user && (
            <Button
              key="toggle"
              type="primary"
              icon={<CheckCircleIcon className="h-4 w-4" />}
              onClick={() => handleToggleStatus("disable")}
              loading={toggleResponse.isLoading}
              danger
            >
              Disable User
            </Button>
          ),
        ]}
      >
        {userResponse.isLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
            <p style={{ marginTop: "16px" }}>Loading user details...</p>
          </div>
        ) : user ? (
          <div>
            {/* User Header */}
            <Card size="small" className="mb-4">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "50%",
                      background: "linear-gradient(45deg, #f97316, #dc2626)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "24px",
                      fontWeight: "bold",
                      marginRight: "16px",
                    }}
                  >
                    {user.first_name?.charAt(0) || "?"}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: "18px" }}>
                      {user.first_name} {user.last_name}
                    </h3>
                    <div style={{ marginTop: "4px" }}>
                      <Tag color={user.is_verified ? "green" : "orange"}>
                        {user.is_verified ? "Verified" : "Unverified"}
                      </Tag>
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "#f97316",
                    }}
                  >
                    {user.total_orders}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    Total Orders
                  </div>
                </div>
              </div>
            </Card>

            <Row gutter={16}>
              {/* Personal Information */}
              <Col span={12}>
                <Card
                  title={
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <UserIcon
                        style={{ marginRight: "8px", color: "#f97316" }}
                      />
                      Personal Information
                    </div>
                  }
                  size="small"
                >
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Full Name">
                      {user.first_name} {user.last_name}
                    </Descriptions.Item>
                    <Descriptions.Item label="Phone">
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <PhoneIcon
                          style={{ width: "14px", marginRight: "4px" }}
                        />
                        {user.phone || "Not provided"}
                      </div>
                    </Descriptions.Item>
                    <Descriptions.Item label="Gotra">
                      {user.gotra || "Not provided"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Gan">
                      {user.gan || "Not provided"}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              {/* Address & Account Info */}
              <Col span={12}>
                <Card
                  title={
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <MapPinIcon
                        style={{ marginRight: "8px", color: "#f97316" }}
                      />
                      Address & Account
                    </div>
                  }
                  size="small"
                >
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Address">
                      {user.address || "Not provided"}
                    </Descriptions.Item>
                    <Descriptions.Item label="City">
                      {user.city || "Not provided"}
                    </Descriptions.Item>
                    <Descriptions.Item label="State">
                      {user.state || "Not provided"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Pincode">
                      {user.pincode || "Not provided"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Registered">
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <CalendarIcon
                          style={{ width: "14px", marginRight: "4px" }}
                        />
                        {new Date(user.created_on).toLocaleDateString("en-IN")}
                      </div>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            </Row>

            {/* Recent Orders */}
            <Card
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <ShoppingBagIcon
                    style={{ marginRight: "8px", color: "#f97316" }}
                  />
                  Recent Orders ({recentOrders.length})
                </div>
              }
              size="small"
              className="mt-4"
            >
              {recentOrders.length > 0 ? (
                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {recentOrders.map((order) => (
                    <div
                      key={order.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 0",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: "500" }}>
                          #{order.order_number}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          {new Date(order.created_on).toLocaleDateString(
                            "en-IN"
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: "500" }}>
                          {formatCurrency(order.total_amount)}
                        </div>
                        <Tag
                          color={getStatusColor(order.order_status)}
                          size="small"
                        >
                          {order.order_status?.toUpperCase()}
                        </Tag>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No orders found"
                />
              )}
            </Card>
          </div>
        ) : (
          <Empty description="User not found" />
        )}
      </Modal>
    </>
  );
}

export default UserDetails;
